import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../../i18n/translationKeys';
import { IconClose } from '../../../assets/icons/IconClose';
import { Spinner } from '../../../ui/Spinner/Spinner';
import type { FilePreview } from './types';

interface FileViewerModalProps {
    file: FilePreview | null;
    /** When true, the content area shows a spinner instead of the document (e.g. while the blob is being fetched). */
    loading?: boolean;
    onClose: () => void;
}

function getFileCategory(fileType: string | null, fileName: string): 'image' | 'pdf' | 'unsupported' {
    if (fileType) {
        if (/^image\/(png|jpe?g|gif|webp)$/.test(fileType)) return 'image';
        if (fileType === 'application/pdf') return 'pdf';
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext && ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'unsupported';
}

export const FileViewerModal: React.FC<FileViewerModalProps> = ({ file, loading = false, onClose }) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (!file) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [file, onClose]);

    if (!file) return null;

    const category = getFileCategory(file.fileType, file.fileName);

    return (
        <div
            data-testid="file-viewer-modal"
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={file.fileName}
        >
            <div
                className="bg-primary-50 rounded-lg shadow-strong max-w-6xl w-full mx-l max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-m px-l py-m border-b border-line-subtle">
                    <span className="text-s font-normal text-primary-1200 truncate">{file.fileName}</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="-m-xs p-xs shrink-0 text-primary-700 hover:text-primary-1200 cursor-pointer transition-colors"
                        aria-label={t(TranslationKey.FileViewer.Close)}
                    >
                        <IconClose className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto flex items-center justify-center p-l min-h-0">
                    {loading && (
                        <Spinner className="w-6 h-6" sparks label={t(TranslationKey.Common.Loading)} />
                    )}
                    {!loading && category === 'image' && (
                        <img
                            src={file.url}
                            alt={file.fileName}
                            className="max-w-full max-h-full object-contain"
                        />
                    )}
                    {!loading && category === 'pdf' && (
                        <embed
                            src={file.url}
                            type="application/pdf"
                            className="w-full min-h-[75vh]"
                        />
                    )}
                    {!loading && category === 'unsupported' && (
                        <p className="text-s text-primary-700">
                            {t(TranslationKey.FileViewer.UnsupportedType)}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
